import { JSX, MouseEventHandler, useState } from "react";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { FlyoutLink } from "@app/shared/Flyouts";
import { NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { StandardColumnWidths } from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityConstants";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAdequacyImpactProvider, NetworkIntegrityAdequacyImpactProviderGap } from "@app/projects/network-assessment/network-integrity/models";
import { generateProviderHubLink, PROVIDER_HUB_UNAVAILABLE_TOOLTIP } from "@app/projects/network-assessment/network-integrity/helpers/providerHubLinkHelper";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { Waffles } from "@app/shared/waffles/Waffles";

interface NetworkIntegrityAdequacyImpactGapFlyoutProps {
  projectId: string,
  projectRunConfigurationId: string,
  gap: NetworkIntegrityAdequacyImpactProviderGap,
  isProviderHubAvailable: boolean,
  isProviderHubDataReady: boolean
}

interface NetworkIntegrityAdequacyImpactProviderTableData {
    [wafflesKeyColumnName]: string;
    npi: string;
    providerName: JSX.Element;
    address: JSX.Element;
    city: string
    state: string;
    zip: string;
    alertOrWarnings: JSX.Element
}

const NetworkIntegrityAdequacyImpactGapFlyout = (props: NetworkIntegrityAdequacyImpactGapFlyoutProps): JSX.Element => {
  const networkIntegrityService = new NetworkIntegrityService();
  const [providers, setProviders] = useState<NetworkIntegrityAdequacyImpactProvider[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

    /* oxlint-disable sort-keys */
    const getTableDataItem = (provider: NetworkIntegrityAdequacyImpactProvider): NetworkIntegrityAdequacyImpactProviderTableData => {

      const link = !props.isProviderHubAvailable
        ? <span>{provider.providerName}</span>
        : props.isProviderHubDataReady
        ? generateProviderHubLink(props.projectId, props.projectRunConfigurationId, provider)
        : <TooltipWrapper text={PROVIDER_HUB_UNAVAILABLE_TOOLTIP}>
          <a href="#" onClick={(e) => e.preventDefault()}>{provider.providerName} <QesIcon iconName={QesIcons.ShareV4} description="open link in new tab" /></a>
        </TooltipWrapper>;

      return {
        [wafflesKeyColumnName]: `${provider.providerSetVersionId}|${provider.providerId}`,
        npi: provider.npi,
        providerName: link,
        address: (provider.address2 ? <span>{provider.address1}<br />{provider.address2}</span> : <span>{provider.address1}</span>),
        city: provider.city,
        state: provider.state,
        zip: provider.zip,
        alertOrWarnings: formatAlertOrWarnings(provider.alertOrWarnings)
      }
    }

    const renderTable = () => {
      const tableData = providers.map(p => getTableDataItem(p)); 

      const columnConfig: ColumnConfig = {
        npi: {
          displayText: "NPI",
          maxWidth: StandardColumnWidths.npi,
          minWidth: StandardColumnWidths.npi,
          testId: "gap-npi",
          sizeContentToFit: false
        },
        providerName: {
          displayText: "Name",
          maxWidth: StandardColumnWidths.providerName,
          minWidth: StandardColumnWidths.providerName,
          testId: "gap-provider-name",
        },
        address: {
          displayText: "Address",
          maxWidth: StandardColumnWidths.address1,
          minWidth: StandardColumnWidths.address1,
          testId: "gap-provider-addrss",
        },
        city: {
          displayText: "City",
          maxWidth: StandardColumnWidths.city,
          minWidth: StandardColumnWidths.city,
          testId: "gap-city",
        },
        state: {
          displayText: "State",
          testId: "gap-state",
          sizeContentToFit: false
        },
        zip: {
          displayText: "Zip",
          maxWidth: StandardColumnWidths.zip,
          minWidth: StandardColumnWidths.zip,
          testId: "gap-zip",
          sizeContentToFit: false
        },
        alertOrWarnings: {
          displayText: "Alert / Warning",
          testId: "gap-alert-or-warning",
          noSort: true,
        },
      };

      return (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Waffles<NetworkIntegrityAdequacyImpactProviderTableData>
            columns={columnConfig}
            data={tableData}
            loading={isLoading}
            testId={`provider-gap-table`}
          />
        </div>
      );
    }
    /* oxlint-enable sort-keys */

  const renderGapHeader = (): string => {
    if (props.gap.zipCode) {
      return `Gap Details: ${props.gap.zipCode} ${props.gap.primaryCounty ?? ""}, ${props.gap.state} - ${props.gap.specialty}`;
    }
    return `Gap Details: ${props.gap.countySSA} ${props.gap.county}, ${props.gap.state} - ${props.gap.specialty}`;
  }

  const renderFlyoutBody = (): JSX.Element => {
    return(
      <>
        <div className="row">
          <h3>{renderGapHeader()} </h3>
        </div>
        <hr />
        {isLoading
          ? <LoadingSpinner />
          : renderTable()}
      </>
    );
  }

  const handleOnOpen: MouseEventHandler = () => {
    setIsLoading(true);
    const pairs = props.gap.providers.map(p => { return `${p.providerId}|${p.providerSetVersionId}`});
    
    networkIntegrityService.getAdequacyImpactProviderGapDetails(Number(props.projectId), Number(props.projectRunConfigurationId), NetworkIntegrityProviderTypes.Servicing, pairs)
            .then(result => {
              setProviders(result);
            })
            .finally(() => {
              setIsLoading(false);
            });
  }

  const formatAlertOrWarnings = (alertsOrWarnings: string[]): JSX.Element => {
    const rows = alertsOrWarnings.map((el, index) => (
      <>
        {index !== 0 ? <br /> : ""}
        <span>{el}</span>
      </>
    ));

    return <div>{rows}</div>;
  }

  return (
    <FlyoutLink
        classNames="qes-button qes-button-secondary"
        content={{
          body: renderFlyoutBody(),
          title: "Adequacy Impact",
        }}
        onOpen={handleOnOpen}
        panelId={`ni-adequacy-impact-gap-detail__flyout-panel`}
        size="60"
        testId={`ni-adequacy-impact-gap-detail__flyout-panel-test`}
      >
      View Details
    </FlyoutLink>
  );

}

export { NetworkIntegrityAdequacyImpactGapFlyout };